import { useEffect, type RefObject } from 'react';

/**
 * Reset scroll position on orientation change (iOS Safari: controls hide behind the sticky
 * header). Safari animates rotation over ~300-400ms and may adjust scroll AFTER an immediate
 * reset, so reset both immediately and after the animation settles.
 *
 * Every viewport-locked table session wants this, so it travels with the frame rather than
 * being re-derived per page.
 *
 * @param scrollRef The session's own scroll container (the window is reset alongside it).
 */
export function useOrientationScrollReset(scrollRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const mql = window.matchMedia('(orientation: portrait)');
    let timerId: ReturnType<typeof setTimeout>;
    const handler = () => {
      const resetScroll = () => {
        window.scrollTo(0, 0);
        scrollRef.current?.scrollTo(0, 0);
      };
      resetScroll();
      requestAnimationFrame(resetScroll);
      timerId = setTimeout(resetScroll, 400);
    };
    mql.addEventListener('change', handler);
    return () => {
      mql.removeEventListener('change', handler);
      clearTimeout(timerId);
    };
  }, [scrollRef]);
}
